import auth from '../utils/auth';
import Head from "../components/Head";
import { scrollToTop } from '../hooks/useScrollToTop';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import '../styles/OrderConfirmedPage.css';

const OrderConfirmed = () => {
    const [order, setOrder] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState('');
    // eslint-disable-next-line
    const [isGuest, setIsGuest] = useState(false);
    const [showGuestForm, setShowGuestForm] = useState(false);
    const [formError, setFormError] = useState(null);
    const { orderId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const fetchGuestOrder = useCallback(async (guestEmail) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/orders/guest/${orderId}?email=${encodeURIComponent(guestEmail)}`
            );

            if (!response.ok) {
                if (response.status === 403 || response.status === 404) {
                    throw new Error('Ingen beställning hittades med detta ordernummer och e-post.');
                } else {
                    throw new Error('Failed to fetch order');
                }
            }

            const data = await response.json();

            if (!data.orderItems) {
                throw new Error('Ingen beställning hittades med detta ordernummer och e-post.');
            }

            setOrder(data);
            setIsGuest(true);
            setShowGuestForm(false);
            setLoading(false);
        } catch (error) {
            setFormError(error.message || 'Det gick inte att hämta orderinformation. Vänligen försök igen senare.');
            setShowGuestForm(true);
            setLoading(false);
        }
    }, [orderId]);

    useEffect(() => {
        const fetchOrder = async () => {
            if (auth.isAuthenticated()) {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/orders/authenticated/${orderId}`, {
                        headers: {
                            'Authorization': `Bearer ${auth.getToken()}`
                        }
                    });

                    if (response.status === 404 || response.status === 403) {
                        setShowGuestForm(true);
                        setLoading(false);
                        return;
                    }

                    if (!response.ok) {
                        throw new Error('Failed to fetch order');
                    }

                    const data = await response.json();

                    if (!data.orderItems || !Array.isArray(data.orderItems)) {
                        throw new Error('Incomplete order data received');
                    }

                    setOrder(data);
                    setIsGuest(false);
                    setLoading(false);
                } catch (error) {
                    setError(error.message || 'Det gick inte att hämta orderinformation. Vänligen försök igen senare.');
                    setLoading(false);
                }
            } else {
                const guestEmail = location.state?.guestEmail;
                const isNewOrder = location.state?.isNewOrder;

                if (guestEmail && isNewOrder) {
                    setEmail(guestEmail);
                    fetchGuestOrder(guestEmail);
                } else {
                    try {
                        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/orders/${orderId}`);

                        if (!response.ok) {
                            throw new Error('Failed to fetch order');
                        }

                        const data = await response.json();

                        if (!data.orderItems) {
                            if (data.isGuestOrder) {
                                setShowGuestForm(true);
                                setLoading(false);
                            } else {
                                navigate('/logga-in', { state: { redirect: `/order/${orderId}` } });
                            }
                        } else {
                            setOrder(data);
                            setIsGuest(data.isGuestOrder);
                            setLoading(false);
                        }
                    } catch (error) {
                        setError(error.message || 'Det gick inte att hämta orderinformation. Vänligen försök igen senare.');
                        setLoading(false);
                    }
                }
            }
        };

        fetchOrder();
    }, [orderId, navigate, location.state, fetchGuestOrder]);

    const handleGuestSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);
        setLoading(true);

        if (!email) {
            setFormError('E-postadress krävs');
            setLoading(false);
            return;
        }

        fetchGuestOrder(email);
    };

    const calculateSubtotal = () => {
        if (!order || !order.orderItems || !Array.isArray(order.orderItems) || order.orderItems.length === 0) {
            return 0;
        }
        return order.orderItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    };

    const calculateTotal = () => {
        if (!order || !order.orderItems || !Array.isArray(order.orderItems) || order.orderItems.length === 0) {
            return 0;
        }

        const subtotal = calculateSubtotal();
        let shippingCost = 65;

        if (order.shippingCost !== undefined) {
            shippingCost = order.shippingCost;
        }
        else if (order.orderItems.length > 0 && order.orderItems[0] && order.orderItems[0].shippingCost !== undefined) {
            shippingCost = order.orderItems[0].shippingCost || 0;
        }

        return subtotal + shippingCost;
    };

    const translateStatus = (status) => {
        switch (status) {
            case 'pending':
                return 'Behandlas';
            case 'shipped':
                return 'Skickad';
            case 'returned':
                return 'Returnerad';
            default:
                return "";
        }
    };

    const handleHomeClick = (event) => {
        event.preventDefault();
        navigate('/');
        scrollToTop();
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-box">
                    <div className="loading-text">Laddar orderinformation...</div>
                </div>
            </div>
        );
    }

    if (showGuestForm) {
        return (
            <div className="order-page">
                <Head title={`EssayWriter | Order ${orderId}`} />
                <div className="order-container guest-form">
                    <h1>Se din beställning</h1>
                    <p>Vänligen ange e-postadressen du använde vid beställningen för att se dina orderdetaljer.</p>

                    <form onSubmit={handleGuestSubmit} className="guest-order-form">
                        {formError && <div className="form-error">{formError}</div>}
                        <div className="form-group">
                            <label htmlFor="email">E-post</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Ange din e-postadress"
                                required
                            />
                        </div>
                        <button type="submit" className="submit-button">
                            Visa min order
                        </button>
                    </form>

                    <p className="order-login-suggestion">
                        Har du redan ett konto? <a href="/logga-in">Logga in</a>
                    </p>

                    <Link to="/" className="back-to-home" onClick={handleHomeClick}>Återgå till startsidan</Link>
                </div>
            </div>
        );
    }

    if (error) {
        navigate('/404');
        return null;
    }

    if (!order || !order.orderItems || !Array.isArray(order.orderItems)) {
        return (
            <div className="order-page">
                <Head title="EssayWriter | Order ej hittad" />
                <div className="order-container error">
                    <h1>Ordern kunde inte hittas</h1>
                    <p>Vi kunde inte hitta den begärda ordern. Kontrollera att du har angett rätt ordernummer.</p>
                    <Link to="/" className="back-to-home" onClick={handleHomeClick}>Återgå till startsidan</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="order-page">
            <Head title={`EssayWriter | Order ${orderId}`} />
            <div className="order-container">
                <h1 className="order-title">Kvitto / Orderbekräftelse</h1>
                <p className="order-number">Ordernummer: {order.orderId}</p>
                <div className="order-content">
                    <div className="order-details-section">
                        <h2>Orderdetaljer</h2>
                        <div className="order-info">
                            <div className="order-info-item">
                                <span>Datum:</span>
                                <span>{new Date(order.createdAt).toLocaleDateString()}</span>
                            </div>
                            <div className="order-info-item">
                                <span>Status:</span>
                                <span>{translateStatus(order.status)}</span>
                            </div>
                            <div className="order-info-item">
                                <span>E-post:</span>
                                <span>{order.email}</span>
                            </div>
                            <div className="order-info-item">
                                <span>Gästbetalning:</span>
                                <span>{order.isGuestOrder ? 'Ja' : 'Nej'}</span>
                            </div>
                        </div>

                        <h3>Leveransdetaljer</h3>
                        <div className="shipping-address">
                            {order.shippingAddress && order.shippingAddress.careOf && <p>C/O {order.shippingAddress.careOf}</p>}
                            {order.shippingAddress && <p>{order.shippingAddress.street}</p>}
                            {order.shippingAddress && <p>{order.shippingAddress.postalCode} {order.shippingAddress.region}</p>}
                            {order.shippingAddress && <p>{order.shippingAddress.country}</p>}
                        </div>
                    </div>

                    <div className="order-items-section">
                        <h2>Din beställning</h2>
                        <div className="order-items-container">
                            {order.orderItems && Array.isArray(order.orderItems) && order.orderItems.map((item, index) => (
                                <div key={index} className="order-item">
                                    <div className="item-details">
                                        <div className="item-name">{item.productId}</div>
                                        <div className="item-quantity">
                                            <span>Antal: {item.quantity}</span>
                                        </div>
                                    </div>
                                    <div className="item-price">{item.price} kr</div>
                                </div>
                            ))}
                        </div>

                        <div className="order-summary">
                            <div className="order-summary-item">
                                <span>Delsumma:</span>
                                <span>{calculateSubtotal()} kr</span>
                            </div>
                            <div className="order-summary-item">
                                <span>Frakt:</span>
                                <span>{order.shippingCost !== undefined ? order.shippingCost :
                                    (order.orderItems && order.orderItems.length > 0 && order.orderItems[0] &&
                                        order.orderItems[0].shippingCost !== undefined ? order.orderItems[0].shippingCost : 0)} kr</span>
                            </div>
                            <div className="order-summary-item total">
                                <span>Totalt:</span>
                                <span>{calculateTotal()} kr</span>
                            </div>
                        </div>
                    </div>
                </div>

                <Link to="/" className="back-to-home" onClick={handleHomeClick}>Återgå till startsidan</Link>
            </div>
        </div>
    );
};

export default OrderConfirmed;